import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import damService from '../services/dam/damService';
import log from '../services/logger/log';
import {selectIsOnline} from '../state/selectors/global';
import {
    selectActiveDusn,
    selectCurrentHolderDusn,
    selectIsDeviceConnectInProgress,
    selectIsDeviceSyncInProgress,
    selectLastConnectionStatusUpdateDate,
} from '../state/selectors/iotDevices';
import {selectIsOnboardingInProgress} from '../state/selectors/session';
import {getState} from '../state/store';

const UPDATE_INTERVAL = 60_000; // 1 min

const useUpdateDeviceStatus = (dusn) => {
    const lastConnectionStatusUpdateDate = useSelector(selectLastConnectionStatusUpdateDate(dusn));
    const isOnboardingInProgress = useSelector(selectIsOnboardingInProgress);
    let intervalId;
    let timeoutId;

    const updateDeviceStatus = () => {
        const isOnline = selectIsOnline(getState());

        if (!isOnline) {
            log.debug(`useUpdateDeviceStatus: Skip update: offline, dusn: ${dusn}`);
            return;
        }

        const activeDusn = selectActiveDusn(getState());
        const currentHolderDusn = selectCurrentHolderDusn(getState());
        const isCurrentDevice = dusn === activeDusn || dusn === currentHolderDusn;

        if (isCurrentDevice && isOnboardingInProgress) {
            log.debug(`useUpdateDeviceStatus: Skip update: onboarding in progress for current device, dusn: ${dusn}`);
            return;
        }

        const isDeviceConnectInProgress = selectIsDeviceConnectInProgress(dusn)(getState());
        const isDeviceSyncInProgress = selectIsDeviceSyncInProgress(dusn)(getState());

        if (isDeviceConnectInProgress || isDeviceSyncInProgress) {
            log.debug(`useUpdateDeviceStatus: Skip update: connect or sync in progress, dusn: ${dusn}`);
            return;
        }

        log.debug(`useUpdateDeviceStatus: Start update, dusn: ${dusn}`);

        damService.getDeviceConnectionStatus(dusn);
    };

    useEffect(() => {
        log.debug(`useUpdateDeviceStatus: Schedule status update, dusn: ${dusn}`);

        // Schedule the first update after a delay
        timeoutId = setTimeout(() => {
            updateDeviceStatus();

            // Start the interval for updates
            intervalId = setInterval(updateDeviceStatus, UPDATE_INTERVAL);
        }, UPDATE_INTERVAL);

        return () => {
            clearTimeout(timeoutId);

            if (intervalId) clearInterval(intervalId);
        };
    }, [lastConnectionStatusUpdateDate, isOnboardingInProgress]);
};

export default useUpdateDeviceStatus;
